import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NewspaperIcon, PlusIcon, ArrowPathIcon, ArchiveBoxArrowDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { listNews, migrateLegacyNews, apiError, type LibraryStatus, type NewsListItem, type NewsListResponse } from '../../api/news'
import { CATEGORIES, alpha } from './newsTheme'
import { formatWall, formatDate, utcIsoToWall } from './newsTime'

type Filter = 'all' | LibraryStatus
const FILTERS: Array<[Filter, string]> = [['all', 'All'], ['published', 'Published'], ['scheduled', 'Scheduled'], ['draft', 'Draft'], ['expired', 'Expired']]
const STATUS_PILL: Record<LibraryStatus, [string, string]> = {
  published: ['Published', 'bg-emerald-50 text-emerald-700'],
  scheduled: ['Scheduled', 'bg-blue-50 text-blue-700'],
  draft: ['Draft', 'bg-amber-50 text-amber-700'],
  expired: ['Expired', 'bg-slate-100 text-slate-600'],
}

function when(it: NewsListItem): string {
  const company = it.tz_mode === 'company'
  const show = (v: string | null) => {
    if (!v) return ''
    if (company) return `${formatWall(v)} company time`
    const tz = it.tz || 'UTC'
    return `${formatWall(utcIsoToWall(v, tz))} ${tz === 'UTC' ? 'UTC' : tz.split('/').pop()?.replace(/_/g, ' ')}`
  }
  const hides = it.expires_at ? ` · hides ${company ? formatWall(it.expires_at).split(',')[0] : formatDate(it.expires_at)}` : ''
  switch (it.library_status) {
    case 'draft': return it.starts_at ? `Not published · set to show ${show(it.starts_at)}` : 'Not scheduled'
    case 'scheduled': return `Shows ${show(it.starts_at)}${hides}`
    case 'expired': return `Expired ${company ? formatWall(it.expires_at || '').split(',')[0] : formatDate(it.expires_at)}`
    default: return `Published ${formatDate(it.published_at || (company ? null : it.starts_at))}${hides}`
  }
}

function LegacyMigration({ onDone, onClose }: { onDone: () => void; onClose: () => void }) {
  const [report, setReport] = useState<Awaited<ReturnType<typeof migrateLegacyNews>> | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [applied, setApplied] = useState(false)
  const run = async (dryRun: boolean) => {
    setBusy(true); setError('')
    try {
      setReport(await migrateLegacyNews(dryRun))
      if (!dryRun) { setApplied(true); onDone() }
    } catch (err) { setError(apiError(err, 'Migration failed')) } finally { setBusy(false) }
  }
  useEffect(() => { run(true) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <ArchiveBoxArrowDownIcon className="w-5 h-5 text-slate-500" />
        <h2 className="font-semibold text-slate-900">Move old announcements into News</h2>
        <button type="button" onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700" aria-label="Close"><XMarkIcon className="w-5 h-5" /></button>
      </div>
      <p className="text-sm text-slate-600">
        Copies sent to each company become one item, HTML becomes content blocks, and reads carry over. Safe to run again: anything already moved is skipped.
        After it runs, installed apps and the admin portal read News through their old endpoints.
      </p>
      {busy && !report && <p className="text-sm text-slate-500">Checking…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {report && (
        <>
          <p className="text-sm text-slate-800">
            {report.legacyRows} old rows → <b>{report.groups}</b> announcements. {report.alreadyMigrated} already moved,{' '}
            <b>{report.toCreate}</b> {applied ? 'moved now' : 'to move'}. {report.readsFound} reads {applied ? `(${report.readsCopied} copied)` : 'to carry over'}.
          </p>
          {report.preview.length > 0 && (
            <div className="max-h-60 overflow-auto border border-slate-100 rounded-lg">
              <table className="w-full text-[12.5px]">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
                  <tr><th className="text-left font-semibold px-2 py-1.5">Title</th><th className="text-left font-semibold px-2">Companies</th><th className="text-left font-semibold px-2">Status</th><th className="text-left font-semibold px-2">Notes</th></tr>
                </thead>
                <tbody>
                  {report.preview.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1.5">{p.title}</td>
                      <td className="px-2">{p.companies === 'all' ? 'All' : p.companies.length}</td>
                      <td className="px-2">{p.status}</td>
                      <td className="px-2 text-slate-500">{p.blocks} blocks{p.warnings.length ? ` · ${p.warnings.length} warning(s)` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!applied && (
            <button type="button" disabled={busy} onClick={() => run(false)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {busy ? 'Moving…' : `Move ${report.toCreate} announcement(s)`}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default function NewsLibrary() {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<Filter>('all')
  const [data, setData] = useState<NewsListResponse | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [showMigration, setShowMigration] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try { setData(await listNews(filter)); setError('') } catch (err) { setError(apiError(err, 'Could not load news')) } finally { setLoading(false) }
  }, [filter])
  useEffect(() => { load() }, [load])

  const counts = data?.counts

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-[1280px] mx-auto">
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-2.5">
        <span className="w-8 h-8 rounded-lg bg-blue-50 grid place-items-center"><NewspaperIcon className="w-[18px] h-[18px] text-blue-600" /></span>
        <h1 className="text-[17px] font-semibold text-slate-900">News</h1>
        <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-slate-50 p-0.5 sm:ml-3">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold ${filter === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {label} {counts ? counts[k] : ''}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50" aria-label="Refresh"><ArrowPathIcon className="w-4 h-4" /></button>
          <button type="button" onClick={() => setShowMigration(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700">
            <ArchiveBoxArrowDownIcon className="w-4 h-4" />Old announcements
          </button>
          <button type="button" onClick={() => navigate('/announcements/new')} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700">
            <PlusIcon className="w-4 h-4" />New announcement
          </button>
        </div>
      </div>

      {showMigration && <LegacyMigration onDone={load} onClose={() => setShowMigration(false)} />}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}
      {loading && !data && <p className="text-sm text-slate-500 px-1">Loading…</p>}
      {data && data.items.length === 0 && !loading && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl px-4 py-10 text-center text-sm text-slate-500">
          {filter === 'all' ? 'No announcements yet.' : `No ${filter} announcements.`}
        </div>
      )}

      <div className="space-y-2.5">
        {data?.items.map((it) => {
          const c = CATEGORIES[it.category]
          const [statusLabel, statusCls] = STATUS_PILL[it.library_status]
          const openedPct = it.stats.reached ? Math.round((it.stats.opened / it.stats.reached) * 100) : 0
          return (
            <div key={it.id} className="bg-white border border-slate-200 rounded-xl grid grid-cols-[4px_minmax(0,1fr)] md:grid-cols-[4px_minmax(0,1fr)_auto] overflow-hidden">
              <span style={{ background: c.color }} />
              <button type="button" onClick={() => navigate(`/announcements/${it.id}`)} className="text-left px-3.5 py-3 flex flex-col gap-1 min-w-0 hover:bg-slate-50/60">
                <div className="flex flex-wrap items-center gap-2">
                  <b className="text-[14.5px] text-slate-900">{it.title}</b>
                  <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${statusCls}`}>{statusLabel}</span>
                </div>
                <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5" style={{ background: alpha(c.color, 0.12), color: c.color }}>
                    <c.Icon className="w-3 h-3" />{c.label}
                  </span>
                  <span>{it.where}</span><span>·</span><span>{when(it)}</span>
                </div>
              </button>
              <div className="col-start-2 md:col-start-auto px-3.5 py-3 flex items-center gap-5 border-t md:border-t-0 md:border-l border-slate-100">
                {it.stats.reached > 0 ? (
                  <>
                    <div><b className="block text-[17px] tabular-nums text-slate-900">{it.stats.reached.toLocaleString()}</b><span className="text-[11px] text-slate-500">Reached</span></div>
                    <div>
                      <b className="block text-[17px] tabular-nums text-slate-900">{openedPct}%</b><span className="text-[11px] text-slate-500">Opened</span>
                      <div className="h-[5px] w-[90px] rounded bg-slate-100 overflow-hidden mt-0.5"><i className="block h-full bg-emerald-600" style={{ width: `${openedPct}%` }} /></div>
                    </div>
                    <div><b className="block text-[17px] tabular-nums text-slate-900">{it.stats.cta.toLocaleString()}</b><span className="text-[11px] text-slate-500">Tapped button</span></div>
                  </>
                ) : (
                  <span className="text-xs text-slate-400">No reads yet</span>
                )}
                <button type="button" onClick={() => navigate(`/announcements/${it.id}`)} className="px-2.5 py-1 text-xs font-semibold rounded-md border border-slate-300 bg-white hover:bg-slate-50">Edit</button>
              </div>
            </div>
          )
        })}
      </div>

      <p className="text-xs text-slate-500 bg-white border border-dashed border-slate-300 rounded-xl px-3 py-2.5">
        <b className="text-slate-700">One row per announcement.</b> Sending to every company no longer makes a copy per company, so editing or unpublishing it
        changes it everywhere at once, and companies added later see it. Reach and opens are counted per person, with a per-company breakdown when you open a row.
      </p>
    </div>
  )
}
